<template>
  <app-content>
    <b-container fluid>
      <step-by-step
        horizontal
        :steps="steps"
        :loading="loadingHandler"
        :current-step.sync="step"
        :next-disabled="!canForward"
        :conclude-disabled="!completedForm"
        :baseDataLoader="reportLoader"
        :dropdownButton="true"
        :exportXlsxLoading="exportXlsxLoading"
        :saveTemplateLoading="saveTemplateLoading"
        :saveTemplateButton="true"
        @saveTemplate="openModal"
        @exportXlsx="exportToXlsx"
        @conclude="saveReport(true, 'view')"
      >

        <installation-tab
          v-model="payload.installations"
          slot="content:installation"
          :backup="installationsIds"
          @update:installations="updateInstallation"
        />

        <asset-and-maneuver
          v-model="payload.maneuvers"
          slot="content:asset-and-maneuver"
          :loading="loadingHandler"
          :selected-backup="payload.maneuvers"
          :filter-backup="assetAndManeuverData.filter"
          :installation-filter="installationsIds"
          :maneuversList="assetAndManeuverData.maneuversList"
          :modules="assetAndManeuverData.modulesList"
          :backup="payload"
          @get-maneuvers="getManeuvers"
          @get-modules="getModules"
          @get-references="updateReferenceVariables"
          @get-variables="updateVariablesIds"
          @clear-grid="clearManeuversGrid"
          @save-filter-backup="saveFilterBackup"
          @initialize-references="initializeReferences"
        />
        <template slot="bottomPreviousPanel">
          <b-button
            v-if="canCancel"
            class="profile-edit__cancel-button"
            variant="secondary"
            @click="cancel()"
          > {{ $t('general.cancel') }}
          </b-button>
        </template>
      </step-by-step>
    </b-container>
    <save-template-modal
      v-model="payload.reportName"
      :show-modal="showModal"
      :is-loading="!!loading"
      @close-modal="closeModal"
      @save="saveTemplate"
    />
  </app-content>
</template>

<script>
import messagesDefinitions from '@/services/messages/message-definitions.json';
import StepByStep from '@/components/stepByStep/index';
import OscillographyService from '@/domain/services/generalPanel/reports/oscillography';
import SaveTemplateModal from '@/domain/components/generalPanel/reports/oscillography/newReport/SaveTemplateModal';
import InstallationTab from '@/domain/components/generalPanel/reports/oscillography/newReport/InstallationTab';
import { formatter as dateTimeFormatter, toTimeZone } from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import AssetAndManeuver from './assetAndManeuver';

export default {
  name: 'NewReport',
  components: {
    InstallationTab,
    SaveTemplateModal,
    StepByStep,
    AssetAndManeuver,
  },
  data: () => ({
    showModal: false,
    loading: 0,
    reportLoader: false,
    assetTypeId: '',
    assetAndManeuverData: {
      maneuversList: [],
      modulesList: [],
      filter: {
        assetType: '',
        assets: [],
        modules: [],
        maneuverTypes: [],
        maneuverSubTypes: [],
        initial: {
          date: null,
          time: '00:00:00',
        },
        final: {
          date: null,
          time: '23:59:59',
        },
      },
    },
    payload: {
      reportName: '',
      installations: [],
      maneuvers: [],
      variables: [],
      referenceVariables: [],
      useOscillographyReference: true,
    },
    step: 0,
    steps: [
      {
        labelPath: 'actions.link-installations',
        icon: 'fas fa-broadcast-tower',
        name: 'installation',
      },
      {
        labelPath: 'reports.asset-and-maneuver',
        icon: 'fas fa-gopuram',
        name: 'asset-and-maneuver',
      },
    ],
    exportXlsxLoading: false,
    saveTemplateLoading: false,
    idViewReport: '',
    originalTemplateName: null,
  }),
  computed: {
    loadingHandler() {
      return this.loading > 0;
    },
    assetType() {
      return this.assetAndManeuverData.filter.assetType;
    },
    canCancel() {
      return this.step === 0;
    },
    installationsIds() {
      return (this.payload.installations || []).map(({ installationId }) => installationId);
    },
    showSaveTemplate() {
      return this.step === 1;
    },
    canForward() {
      switch (this.step) {
        case 0:
          return (this.payload.installations || []).length;
        case 1:
          return (this.payload.maneuvers || []).length;
        default:
          return (this.payload.variables || []).length;
      }
    },
    completedForm() {
      if (this.payload.installations?.length > 0
        && this.payload.maneuvers.length > 0
        && this.payload.variables.length > 0
      ) {
        return true;
      }

      return false;
    },
  },
  methods: {
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      if (!this.saveTemplateLoading) {
        this.payload.reportName = this.payload.id ? this.originalTemplateName : '';
      }
      this.showModal = false;
    },
    exportToXlsx() {
      this.exportXlsxLoading = true;
      this.saveReport(true, 'export')
        .then((id) => {
          this.idViewReport = id;
        })
        .finally(() => {
          OscillographyService.downloadReport(this.idViewReport).finally(() => {
            this.exportXlsxLoading = false;
          });
        });
    },
    saveFilterBackup(filter) {
      this.assetAndManeuverData.filter = filter;
    },
    saveTemplate() {
      this.saveTemplateLoading = true;
      return this.saveReport(false, 'saveTemplate')
        .then(() => {
          this.closeModal();
        })
        .finally(() => {
          this.saveTemplateLoading = false;
        });
    },
    mountPayload(saveTemplate) {
      const {
        assetType,
        assets,
        maneuverTypes,
        maneuverSubTypes,
        initialDateAndTime,
        finalDateAndTime,
      } = this.assetAndManeuverData.filter;

      const {
        id,
        maneuvers,
        variables,
        referenceVariables,
        reportName,
        installations,
      } = this.payload;

      const maneuversUtc = (maneuvers || []).map((m) => {
        if (m && typeof m === 'object') {
          return {
            ...m,
            dateAndTime: m.dateAndTime
              ? toTimeZone(m.dateAndTime, timezones.UTC)
              : m.dateAndTime,
          };
        }
        return m;
      });

      return {
        id,
        reportName,
        assetTypeId: assetType,
        transient: saveTemplate,
        template: {
          maneuvers: maneuversUtc,
          variables,
          referenceVariables,
          installations: installations.map((x) => ({
              installationId: x.installationId,
              installationName: x.installationName,
          })),
          assets,
          maneuverTypes,
          maneuverSubTypes,
          initialDate: initialDateAndTime,
          finalDate: finalDateAndTime,
        },
      };
    },
    saveReport(saveTemplate, tipoFuncao) {
      const payloadBuilder = this.mountPayload(saveTemplate);
      this.loading += 1;
      return OscillographyService
        .saveReport(payloadBuilder)
        .then(({ reportId }) => {
          if (payloadBuilder.transient && tipoFuncao === 'view') {
            return this.openReport(reportId);
          }
          if (tipoFuncao === 'saveTemplate') {
            this.$pushAlert({ code: 'MS006' });
            return this.backToList();
          }
          if (tipoFuncao === 'export') {
            return reportId;
          }

          return true;
        })
        .finally(() => {
          this.loading -= 1;
        });
    },
    backToList() {
      return this.$router.push({
        name: 'reports',
        params: {
          activeTabName: 'oscillography',
        },
      });
    },
    openReport(id) {
      const reportId = id || this.payload.id;
      const routeData = this.$router.resolve({
        name: 'clean-view.oscillography.view-report',
        params: {
          reportId,
        },
        query: {
          isNew: true,
        },
      });
      return window.open(routeData.href, '_blank');
    },
    updateVariablesIds(newItems) {
      this.payload.variables = newItems;
    },
    updateReferenceVariables(newItems) {
      this.payload.referenceVariables = newItems;
      this.payload.useOscillographyReference = !!(newItems && newItems.length > 0);
    },
    updateInstallation(value) {
      this.payload.installations = value;
    },
    handleEditPayload(report) {
      this.originalTemplateName = report.reportName;
      let configInstallations = [];

      if ((report.template?.installations || []).length === 0) {
        configInstallations = report.installations;
      } else {
        configInstallations = report.template?.installations;
      }

      this.payload = {
        id: report.id,
        reportName: report.reportName,
        installations: configInstallations,
        maneuvers: (report.template || {}).maneuvers || [],
        variables: this.setUpperCase((report.template || {}).variables),
        referenceVariables: this.setUpperCase((report.template || {}).referenceVariables),
        useOscillographyReference: ((report.template || {}).referenceVariables || []).length > 0,
      };

      this.assetAndManeuverData.filter = {
        assetType: report.assetTypeId,
        assets: this.setUpperCase((report.template || {}).assetsIds),
        modules: [],
        maneuverTypes: report.template?.maneuverTypes,
        maneuverSubTypes: report.template?.maneuverSubTypes,
        initial: {
          date: (report.template?.inicialDate === '0001-01-01T00:00:00')
            ? '' : report.template?.inicialDate,
          time: (report.template?.inicialDate === '0001-01-01T00:00:00')
            ? '00:00:00' : dateTimeFormatter(report.template?.inicialDate, formats.TIME_24_WITH_SECONDS),
        },
        final: {
          date: (report.template?.finalDate === '0001-01-01T00:00:00')
          ? '' : report.template?.finalDate,
          time: (report.template?.finalDate === '0001-01-01T00:00:00')
          ? '23:59:59' : dateTimeFormatter(report.template?.finalDate, formats.TIME_24_WITH_SECONDS),
        },
      };

      if (this.assetAndManeuverData.filter.assets.length > 0) {
        this.getModules(this.assetAndManeuverData.filter.assets);
      }
    },
    setUpperCase(list) {
      if (!list || !list.length) return [];
      return list.map(item => item.toUpperCase());
    },
    extractUniqueModules(maneuvers) {
      if (!maneuvers || !maneuvers.length) return [];

      const moduleIds = maneuvers
        .map(maneuver => maneuver.assetModuleId)
        .filter(moduleId => moduleId)
        .map(moduleId => moduleId.toUpperCase());

      const uniqueModuleIds = [...new Set(moduleIds)];

      return uniqueModuleIds;
    },
    getReportToEdit() {
      this.loading += 1;
      this.reportLoader = true;
      return OscillographyService
        .getReport(this.$route.params.relatorioId)
        .then((report) => {
          if (report.partial) {
            this.$pushAlert({
              code: 'MI106',
            });
          }
          this.handleEditPayload(report);
        })
        .finally(() => {
          this.reportLoader = false;
          this.loading -= 1;
        });
    },
    maneuversMessagesHandler(messages = []) {
      if (!messages.length) return;

      (messages || []).forEach((item) => {
        const { messageCode: code, message } = item;
        const { type } = messagesDefinitions[code];

        switch (code) {
          case 'MA017':
            this.$pushAlert({
              code,
            });
            break;
          case 'ME075':
            this.$pushAlert({
              type,
              message,
            });
            break;
          default:
            break;
        }
      });
    },
    getModules(ativosIds) {
      OscillographyService.getModules(ativosIds)
        .then((modules) => {
          this.assetAndManeuverData.modulesList = modules;

          this.assetAndManeuverData.filter.modules = modules.map(module => module.id);
        });
    },
    getManeuvers(filter, activeManeuvers = []) {
      this.loading += 1;
      this.assetAndManeuverData.filter = {
        ...filter,
      };

      OscillographyService.getManeuvers(filter)
        .then(({ maneuvers, messages = [] }) => {
          if (activeManeuvers.length > 0) {
            this.assetAndManeuverData.maneuversList = maneuvers
              .filter(item => !activeManeuvers.includes(item.oscillographyId));
          } else {
            this.assetAndManeuverData.maneuversList = maneuvers;
          }

          this.maneuversMessagesHandler(messages);
          if (this.payload.variables.length === 0) {
            this.getVariables(filter.assetType, filter.maneuverTypes, filter.modules);
          }

          if (!maneuvers.length) {
            this.$pushAlert({
              code: 'MA009',
            });
          }
        })
        .finally(() => {
          this.loading -= 1;
        });
    },
    getVariables(assetTypeId, maneuverTypes, modules = []) {
      const moduloId = modules && modules.length > 0 ? modules[0] : '';
      OscillographyService.getVariables({ assetTypeId, maneuverTypes, moduloId })
        .then(({ variables }) => {
            this.payload.variables = variables.map((x) => x.id);
        });
    },
    initReport() {
      if (!this.$route.params.relatorioId) return;
      this.getReportToEdit();
    },
    cancel() {
      this.$confirmMessage({
        code: 'MC006',
      }).accepted(() => {
        this.backToList();
      });
    },
    assetTypeChanged() {
      this.clearManeuversGrid();
    },
    installationChanged() {
      this.assetAndManeuverData = {
        maneuversList: [],
        filter: {
          ...this.assetAndManeuverData.filter,
          assets: [],
          maneuverTypes: [],
          maneuverSubTypes: [],
          initial: {
            date: null,
            time: '00:00:00',
          },
          final: {
            date: null,
            time: '23:59:59',
          },
        },
      };
      this.payload.referenceVariables = [];
      this.payload.useOscillographyReference = false;
    },
    clearManeuversGrid() {
      this.assetAndManeuverData.maneuversList = [];
      // this.payload.referenceVariables = [];
    },
    async initializeReferences({ assetType, maneuverTypes, moduloId }) {
      if (this.payload.referenceVariables?.length > 0) {
        return;
      }
      if (this.payload.useOscillographyReference === false) {
        return;
      }
      if (assetType && maneuverTypes?.length > 0 && moduloId !== '') {
        const { referenceVariables } = await OscillographyService.getVariables({
          assetTypeId: assetType,
          maneuverType: maneuverTypes,
          moduloId,
        });

        if (referenceVariables?.length > 0) {
          this.payload.referenceVariables = referenceVariables.map(r => r.id ?? r);
          this.payload.useOscillographyReference = true;
        }
      }
    },
  },
  created() {
    this.initReport();
  },
  watch: {
    installationsIds(newSelection, oldSelection) {
      if (oldSelection.every(item => newSelection.includes(item))) {
        return;
      }
      this.installationChanged();
    },
    assetType() {
      this.assetTypeChanged();
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.configuration
  display: flex
  flex-direction: column
  overflow-y: hidden
  &__table ::v-deep
    overflow-y: hidden
    display: flex
    max-height: 53vh
    table
      display: flex
      flex-direction: column
      min-width: 450px
    tbody
      display: block
      overflow-y: auto
      overflow-x: hidden
    tbody, thead
      tr
        display: table
        width: 100%
        table-layout: fixed
  &__loader
    font-size: 2em
    margin-bottom: 1em
  &__check-wrapper
    display: flex
  ::v-deep &__cell
    &-check
      width: 40px
  +media-min-sm()
  +media-min-md()
  +media-min-lg()
</style>
